import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosConfig';
import { useAuth } from '../context/AuthContext';
import SearchableProductSelect from '../components/SearchableProductSelect';

const Inventory = () => {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState('movements');
  const [movements, setMovements] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adjForm, setAdjForm] = useState({
    reason: '',
    notes: '',
    location_id: '',
    product_id: '',
    uom_id: '',
    quantity: ''
  });
  const [trForm, setTrForm] = useState({
    from_location_id: '',
    to_location_id: '',
    notes: '',
    product_id: '',
    uom_id: '',
    quantity: ''
  });
  const [adjUoms, setAdjUoms] = useState([]);
  const [trUoms, setTrUoms] = useState([]);
  const [newLocName, setNewLocName] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [m, a, t, l, p] = await Promise.all([
        axios.get('/api/inventory/movements'),
        axios.get('/api/inventory/adjustments'),
        axios.get('/api/inventory/transfers'),
        axios.get('/api/inventory/locations'),
        axios.get('/api/products')
      ]);
      setMovements(m.data.movements || []);
      setAdjustments(a.data.adjustments || []);
      setTransfers(t.data.transfers || []);
      setLocations(l.data.locations || []);
      setProducts(p.data.products || []);
      const def = (l.data.locations || []).find((x) => Number(x.is_default)) || (l.data.locations || [])[0];
      if (def) {
        setAdjForm((f) => ({ ...f, location_id: f.location_id || def.id }));
        setTrForm((f) => ({
          ...f,
          from_location_id: f.from_location_id || def.id
        }));
      }
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const loadUoms = async (productId, setter) => {
    if (!productId) {
      setter([]);
      return;
    }
    const p = products.find((x) => x.id === productId);
    if (p?.uoms?.length) {
      setter(p.uoms);
      return;
    }
    try {
      const res = await axios.get(`/api/products/${productId}/uoms`);
      setter(res.data.uoms || []);
    } catch (_) {
      setter([]);
    }
  };

  const submitAdjustment = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/inventory/adjustments', {
        reason: adjForm.reason,
        notes: adjForm.notes,
        location_id: adjForm.location_id || undefined,
        items: [
          {
            product_id: adjForm.product_id,
            uom_id: adjForm.uom_id || undefined,
            quantity: parseFloat(adjForm.quantity)
          }
        ]
      });
      setAdjForm((f) => ({ ...f, product_id: '', uom_id: '', quantity: '', reason: '', notes: '' }));
      setAdjUoms([]);
      await load();
      alert('Adjustment saved');
    } catch (err) {
      alert(err.response?.data?.message || 'Adjustment failed');
    }
  };

  const submitTransfer = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/inventory/transfers', {
        from_location_id: trForm.from_location_id,
        to_location_id: trForm.to_location_id,
        notes: trForm.notes,
        items: [
          {
            product_id: trForm.product_id,
            uom_id: trForm.uom_id || undefined,
            quantity: parseFloat(trForm.quantity)
          }
        ]
      });
      setTrForm((f) => ({ ...f, product_id: '', uom_id: '', quantity: '', notes: '' }));
      setTrUoms([]);
      await load();
      alert('Transfer saved');
    } catch (err) {
      alert(err.response?.data?.message || 'Transfer failed');
    }
  };

  const addLocation = async (e) => {
    e.preventDefault();
    if (!newLocName.trim()) return;
    try {
      await axios.post('/api/inventory/locations', { name: newLocName.trim() });
      setNewLocName('');
      await load();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to add location');
    }
  };

  const formatQty = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return value ?? '';
    return String(Math.round(n * 1000) / 1000);
  };

  if (!isAdmin) {
    return (
      <div className="page-container">
        <div className="container">
          <p>Admin access required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header">
          <h1>Inventory</h1>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {['movements', 'adjustments', 'transfers', 'locations'].map((t) => (
            <button
              key={t}
              type="button"
              className={`btn ${tab === t ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setTab(t)}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
          <button type="button" className="btn btn-secondary" onClick={load} disabled={loading}>
            Refresh
          </button>
        </div>

        {tab === 'movements' && (
          <div className="card" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Product</th>
                  <th>Type</th>
                  <th>Qty (base)</th>
                  <th>Location</th>
                  <th>Ref</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((m) => (
                  <tr key={m.id}>
                    <td>{m.created_at}</td>
                    <td>{m.product_name || m.product_id}</td>
                    <td>{m.movement_type}</td>
                    <td>
                      {formatQty(m.qty_base)} {m.product_unit || ''}
                    </td>
                    <td>{m.location_name || '—'}</td>
                    <td>
                      {m.reference_type || ''} {m.reference_id ? String(m.reference_id).slice(0, 8) : ''}
                    </td>
                  </tr>
                ))}
                {!movements.length && (
                  <tr>
                    <td colSpan={6}>No movements yet</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'adjustments' && (
          <div>
            <form onSubmit={submitAdjustment} className="card" style={{ padding: 16, marginBottom: 20 }}>
              <h3>New adjustment</h3>
              <p style={{ fontSize: 13, color: '#666' }}>
                Use negative quantity to decrease stock. Qty is in the selected UOM; stock updates in base units.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label>Product</label>
                  <SearchableProductSelect
                    products={products}
                    value={adjForm.product_id}
                    onChange={(id) => {
                      setAdjForm((f) => ({ ...f, product_id: id, uom_id: '' }));
                      loadUoms(id, setAdjUoms);
                    }}
                  />
                </div>
                <div className="form-group">
                  <label>UOM</label>
                  <select
                    value={adjForm.uom_id}
                    onChange={(e) => setAdjForm((f) => ({ ...f, uom_id: e.target.value }))}
                  >
                    <option value="">Default (inventory/base)</option>
                    {adjUoms.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.code} {u.is_base ? '(base)' : `×${u.conversion_to_base}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Quantity (+/−)</label>
                  <input
                    type="number"
                    step="any"
                    required
                    value={adjForm.quantity}
                    onChange={(e) => setAdjForm((f) => ({ ...f, quantity: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <select
                    value={adjForm.location_id}
                    onChange={(e) => setAdjForm((f) => ({ ...f, location_id: e.target.value }))}
                  >
                    {locations.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Reason</label>
                  <input
                    type="text"
                    value={adjForm.reason}
                    onChange={(e) => setAdjForm((f) => ({ ...f, reason: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <input
                    type="text"
                    value={adjForm.notes}
                    onChange={(e) => setAdjForm((f) => ({ ...f, notes: e.target.value }))}
                  />
                </div>
              </div>
              <button type="submit" className="btn btn-primary" style={{ marginTop: 12 }}>
                Save adjustment
              </button>
            </form>
            <div className="card" style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Number</th>
                    <th>When</th>
                    <th>Location</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {adjustments.map((a) => (
                    <tr key={a.id}>
                      <td>{a.adjustment_number}</td>
                      <td>{a.created_at}</td>
                      <td>{a.location_name || '—'}</td>
                      <td>{a.reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'transfers' && (
          <div>
            <form onSubmit={submitTransfer} className="card" style={{ padding: 16, marginBottom: 20 }}>
              <h3>New transfer</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label>From</label>
                  <select
                    required
                    value={trForm.from_location_id}
                    onChange={(e) => setTrForm((f) => ({ ...f, from_location_id: e.target.value }))}
                  >
                    {locations.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>To</label>
                  <select
                    required
                    value={trForm.to_location_id}
                    onChange={(e) => setTrForm((f) => ({ ...f, to_location_id: e.target.value }))}
                  >
                    <option value="">Select…</option>
                    {locations.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Product</label>
                  <SearchableProductSelect
                    products={products}
                    value={trForm.product_id}
                    onChange={(id) => {
                      setTrForm((f) => ({ ...f, product_id: id, uom_id: '' }));
                      loadUoms(id, setTrUoms);
                    }}
                  />
                </div>
                <div className="form-group">
                  <label>UOM</label>
                  <select
                    value={trForm.uom_id}
                    onChange={(e) => setTrForm((f) => ({ ...f, uom_id: e.target.value }))}
                  >
                    <option value="">Default</option>
                    {trUoms.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.code} {u.is_base ? '(base)' : `×${u.conversion_to_base}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Quantity</label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    required
                    value={trForm.quantity}
                    onChange={(e) => setTrForm((f) => ({ ...f, quantity: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <input
                    type="text"
                    value={trForm.notes}
                    onChange={(e) => setTrForm((f) => ({ ...f, notes: e.target.value }))}
                  />
                </div>
              </div>
              <button type="submit" className="btn btn-primary" style={{ marginTop: 12 }}>
                Save transfer
              </button>
            </form>
            <div className="card" style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Number</th>
                    <th>When</th>
                    <th>From</th>
                    <th>To</th>
                  </tr>
                </thead>
                <tbody>
                  {transfers.map((t) => (
                    <tr key={t.id}>
                      <td>{t.transfer_number}</td>
                      <td>{t.created_at}</td>
                      <td>{t.from_location_name}</td>
                      <td>{t.to_location_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'locations' && (
          <div>
            <form onSubmit={addLocation} className="card" style={{ padding: 16, marginBottom: 16, display: 'flex', gap: 8 }}>
              <input
                type="text"
                placeholder="New location name"
                value={newLocName}
                onChange={(e) => setNewLocName(e.target.value)}
              />
              <button type="submit" className="btn btn-primary">
                Add location
              </button>
            </form>
            <ul>
              {locations.map((l) => (
                <li key={l.id}>
                  {l.name} {Number(l.is_default) ? '(default)' : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default Inventory;
